import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "../styles/SATRequest.css";
import "../styles/AdminApplications.css";
import { apiClient } from "../services/apiClient";
import PaginationControls from "../components/PaginationControls";
import { TabBar, FilterBar, FilterField, SearchInput, EmptyState } from "../components/ListToolbar";
import { useConfirm } from "../providers/ConfirmProvider";
import { useI18n } from "../providers/I18nProvider";

// İş başvurularını listeler, filtreler, statü/etiket yönetir.
// Erişim sunucu tarafında requireApplicationsAccess ile zaten gateli;
// menü görünürlüğü AdminDashboardLayout'ta tool-permission/role ile kontrol edilir.

// Etiket metinleri i18n'den (applications.status.*) çözülür; burada yalnızca renkler var.
const STATUS_STYLES = {
  new: { color: "#1677ff", bg: "#e6f4ff" },
  approved: { color: "#0a7a3b", bg: "#e6f4ea" },
  pooled: { color: "#a35a00", bg: "#fff4e0" },
  rejected: { color: "#a8071a", bg: "#fff1f0" },
};

const StatusChip = ({ status }) => {
  const { t } = useI18n();
  const def = STATUS_STYLES[status] || STATUS_STYLES.new;
  return (
    <span className="apps-status" style={{ color: def.color, background: def.bg }}>
      {t(`applications.status.${STATUS_STYLES[status] ? status : "new"}`)}
    </span>
  );
};

const TagChip = ({ tag, onRemove }) => {
  const { t } = useI18n();
  return (
    <span className="apps-tag" style={{ background: tag.color || "#1677ff" }}>
      {tag.name}
      {onRemove && (
        <button
          type="button"
          className="apps-tag__remove"
          onClick={() => onRemove(tag)}
          title={t("applications.removeTag")}
          aria-label={t("applications.removeTagNamed", { name: tag.name })}
        >
          ×
        </button>
      )}
    </span>
  );
};

const ApplicationDetail = ({ id, onClose, onUpdated, types, tags }) => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [newTag, setNewTag] = useState("");
  const [busy, setBusy] = useState(false);

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(`/job-applications/${id}`);
      if (seq === loadSeq.current) setData(res.data);
    } catch (err) {
      if (seq === loadSeq.current) setError(err.response?.data?.msg || err.message || t("applications.loadFailed"));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const updateStatus = async (status) => {
    if (busy) return;
    if (
      status === "rejected" &&
      !(await confirm(t("applications.rejectConfirm"), { confirmLabel: t("applications.actions.reject") }))
    )
      return;
    setBusy(true);
    setError("");
    try {
      const res = await apiClient.patch(`/job-applications/${id}/status`, {
        status,
        note: statusNote || undefined,
      });
      setData(res.data);
      setStatusNote("");
      onUpdated && onUpdated();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.updateFailed"));
    } finally {
      setBusy(false);
    }
  };

  const addTag = async () => {
    const name = newTag.trim();
    if (!name || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await apiClient.post(`/job-applications/${id}/tags`, { name });
      setData(res.data);
      setNewTag("");
      onUpdated && onUpdated();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.tagAddFailed"));
    } finally {
      setBusy(false);
    }
  };

  const removeTag = async (tag) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await apiClient.delete(`/job-applications/${id}/tags/${tag.id}`);
      await load();
      onUpdated && onUpdated();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.tagRemoveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const downloadCv = async () => {
    try {
      const res = await apiClient.get(`/job-applications/${id}/cv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", data?.cv_original_name || `cv_${id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.msg || t("applications.cvDownloadFailed"));
    }
  };

  const typeLabel = useMemo(() => {
    if (!data) return "";
    if (data.application_type_name_tr) return data.application_type_name_tr;
    const found = (types || []).find((x) => x.id === data.application_type_id);
    return found?.name_tr || "-";
  }, [data, types]);

  return (
    <>
      <div className="apps-backdrop" onClick={onClose} />
      <div className="apps-drawer" role="dialog" aria-modal="true" aria-label={t("applications.detailAria", { id })}>
        <div className="apps-drawer__head">
          <h3>{t("applications.detailTitle", { id })}</h3>
          <button type="button" className="sat-modal__close" onClick={onClose} aria-label={t("applications.close")}>
            ×
          </button>
        </div>

        {loading && !data && <div>{t("applications.loading")}</div>}
        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}

        {data && (
          <>
            <dl className="apps-meta">
              <dt>{t("applications.detail.applicant")}</dt>
              <dd>{data.applicant_name}</dd>
              <dt>{t("applications.detail.email")}</dt>
              <dd>
                <a href={`mailto:${data.applicant_email}`}>{data.applicant_email}</a>
              </dd>
              <dt>{t("applications.detail.phone")}</dt>
              <dd>
                {data.applicant_phone ? <a href={`tel:${data.applicant_phone}`}>{data.applicant_phone}</a> : "-"}
              </dd>
              <dt>{t("applications.detail.job")}</dt>
              <dd>{data.external_job_title || `#${data.external_job_id || "-"}`}</dd>
              <dt>{t("applications.detail.type")}</dt>
              <dd>{typeLabel}</dd>
              <dt>{t("applications.detail.date")}</dt>
              <dd>{new Date(data.created_at).toLocaleString("tr-TR")}</dd>
              <dt>{t("applications.detail.status")}</dt>
              <dd>
                <StatusChip status={data.status} />
              </dd>
              <dt>{t("applications.detail.cv")}</dt>
              <dd>
                {data.has_cv === false ? (
                  "-"
                ) : (
                  <button type="button" onClick={downloadCv} className="sat-btn sat-btn--primary">
                    {data.cv_original_name || t("applications.downloadCv")}
                  </button>
                )}
              </dd>
            </dl>

            <div className="apps-section">
              <h4>{t("applications.detail.description")}</h4>
              <div className="apps-desc-full">{data.description || <em>{t("applications.none")}</em>}</div>
            </div>

            <div className="apps-section">
              <h4>{t("applications.detail.tags")}</h4>
              <div className="apps-tag-list">
                {(data.tags || []).map((tg) => (
                  <TagChip key={tg.id} tag={tg} onRemove={removeTag} />
                ))}
                {(data.tags || []).length === 0 && <span className="sat-hint">{t("applications.none")}</span>}
              </div>
              <div className="apps-tag-add">
                <input
                  type="text"
                  list="apps-tag-suggestions"
                  className="apps-input"
                  placeholder={t("applications.tagPlaceholder")}
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTag()}
                />
                <datalist id="apps-tag-suggestions">
                  {(tags || [])
                    .filter((tg) => !(data.tags || []).some((x) => x.id === tg.id))
                    .map((tg) => (
                      <option key={tg.id} value={tg.name} />
                    ))}
                </datalist>
                <button type="button" onClick={addTag} disabled={!newTag.trim() || busy} className="sat-btn sat-btn--primary">
                  {t("applications.add")}
                </button>
              </div>
            </div>

            <div className="apps-section">
              <h4>{t("applications.changeStatus")}</h4>
              <textarea
                placeholder={t("applications.notePlaceholder")}
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={2}
                className="apps-input apps-note"
              />
              <div className="apps-status-actions">
                <button type="button" disabled={busy || data.status === "approved"} onClick={() => updateStatus("approved")} className="sat-btn sat-btn--success">
                  {t("applications.actions.approve")}
                </button>
                <button type="button" disabled={busy || data.status === "pooled"} onClick={() => updateStatus("pooled")} className="sat-btn apps-btn-pool">
                  {t("applications.actions.pool")}
                </button>
                <button type="button" disabled={busy || data.status === "rejected"} onClick={() => updateStatus("rejected")} className="sat-btn sat-btn--danger">
                  {t("applications.actions.reject")}
                </button>
                <button type="button" disabled={busy || data.status === "new"} onClick={() => updateStatus("new")} className="sat-btn">
                  {t("applications.actions.reset")}
                </button>
              </div>
              <p className="sat-hint">{t("applications.approveHint")}</p>
            </div>

            <div className="apps-section">
              <h4>{t("applications.detail.history")}</h4>
              <ul className="apps-history">
                {(data.history || []).map((h) => (
                  <li key={h.id}>
                    <div>
                      <strong>
                        {h.old_status
                          ? `${STATUS_STYLES[h.old_status] ? t(`applications.status.${h.old_status}`) : h.old_status} → `
                          : ""}
                        {STATUS_STYLES[h.new_status] ? t(`applications.status.${h.new_status}`) : h.new_status}
                      </strong>
                      {h.changed_by_name && ` · ${h.changed_by_name}`}
                    </div>
                    <div className="sat-hint">
                      {new Date(h.changed_at).toLocaleString("tr-TR")}
                      {h.note && ` · ${h.note}`}
                    </div>
                  </li>
                ))}
                {(data.history || []).length === 0 && <li className="sat-hint">{t("applications.noHistory")}</li>}
              </ul>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const TAB_KEYS = ["", "new", "approved", "pooled", "rejected"];

const AdminApplicationsPage = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [types, setTypes] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("created_desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [openId, setOpenId] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const fetchSeq = useRef(0);
  const closeDetail = useCallback(() => setOpenId(null), []);

  const fetchTypesAndTags = useCallback(async () => {
    try {
      const [tRes, gRes] = await Promise.all([
        apiClient.get("/application-types"),
        apiClient.get("/application-tags"),
      ]);
      setTypes(tRes.data || []);
      setTags(gRes.data || []);
    } catch (err) {
      // type listesini alamamak kritik değil; görmezden gel
    }
  }, []);

  // Arama kutusu: her tuşta istek atma, 350ms bekle.
  useEffect(() => {
    const h = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(h);
  }, [searchInput]);

  const baseParams = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (typeFilter) params.type = typeFilter;
    if (tagFilter) params.tag = tagFilter;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    return params;
  }, [search, typeFilter, tagFilter, fromDate, toDate]);

  const fetchRows = useCallback(async () => {
    const seq = ++fetchSeq.current; // eski cevap yenisini ezmesin
    setLoading(true);
    setError("");
    setForbidden(false);
    try {
      const params = { ...baseParams(), page, pageSize, sort: sortKey };
      if (statusFilter) params.status = statusFilter;
      const res = await apiClient.get("/job-applications", { params });
      if (seq !== fetchSeq.current) return;
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      if (err.response?.status === 403) {
        setForbidden(true);
      } else {
        setError(err.response?.data?.msg || err.message || t("applications.loadFailed"));
      }
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [page, pageSize, statusFilter, sortKey, baseParams, t]);

  // Sekme sayaçları: statü hariç diğer filtrelerle her statünün toplamı
  const fetchCounts = useCallback(async () => {
    try {
      const entries = await Promise.all(
        TAB_KEYS.map(async (st) => {
          const params = { ...baseParams(), page: 1, pageSize: 1 };
          if (st) params.status = st;
          const res = await apiClient.get("/job-applications", { params });
          return [st, res.data?.total || 0];
        })
      );
      setCounts(Object.fromEntries(entries));
    } catch (err) {
      // sayaçlar kritik değil
    }
  }, [baseParams]);

  useEffect(() => {
    fetchTypesAndTags();
  }, [fetchTypesAndTags]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const onUpdated = useCallback(() => {
    fetchRows();
    fetchCounts();
    fetchTypesAndTags();
  }, [fetchRows, fetchCounts, fetchTypesAndTags]);

  const hasActiveFilter = Boolean(searchInput || typeFilter || tagFilter || fromDate || toDate);
  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setTypeFilter("");
    setTagFilter("");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const tabs = useMemo(
    () =>
      TAB_KEYS.map((st) => ({
        key: st || "all",
        label: st ? t(`applications.status.${st}`) : t("applications.tabAll"),
        count: counts[st] ?? null,
        attention: st === "new",
      })),
    [counts, t]
  );

  return (
    <div className="sat-page admin-applications-page">
      <div className="sat-page-header">
        <div>
          <h2>{t("applications.title")}</h2>
          <div className="subtitle">{t("applications.subtitle")}</div>
        </div>
      </div>

      {forbidden && (
        <div className="apps-forbidden" role="alert">
          <strong>{t("applications.forbiddenTitle")}</strong>
          <br />
          {t("applications.forbiddenBody")}
        </div>
      )}
      {error && (
        <div className="error-banner" role="alert">
          {error}{" "}
          <button type="button" className="sat-btn" onClick={onUpdated}>
            {t("applications.refresh")}
          </button>
        </div>
      )}

      <TabBar
        tabs={tabs}
        value={statusFilter || "all"}
        onChange={(k) => {
          setPage(1);
          setStatusFilter(k === "all" ? "" : k);
        }}
        ariaLabel={t("applications.tabsAria")}
      />

      <FilterBar showReset={hasActiveFilter} onReset={resetFilters}>
        <SearchInput value={searchInput} onChange={setSearchInput} placeholder={t("applications.searchPlaceholder")} />
        <select
          className="leaves-filter-input lt-input"
          value={typeFilter}
          onChange={(e) => {
            setPage(1);
            setTypeFilter(e.target.value);
          }}
          aria-label={t("applications.filters.typeAria")}
        >
          <option value="">{t("applications.filters.allTypes")}</option>
          {types.map((ty) => (
            <option key={ty.id} value={ty.id}>
              {ty.name_tr}
            </option>
          ))}
        </select>
        <select
          className="leaves-filter-input lt-input"
          value={tagFilter}
          onChange={(e) => {
            setPage(1);
            setTagFilter(e.target.value);
          }}
          aria-label={t("applications.filters.tagAria")}
        >
          <option value="">{t("applications.filters.allTags")}</option>
          {tags.map((tg) => (
            <option key={tg.id} value={tg.id}>
              {tg.name}
            </option>
          ))}
        </select>
        <FilterField label={t("applications.filters.from")}>
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => {
              setPage(1);
              setFromDate(e.target.value);
            }}
          />
        </FilterField>
        <FilterField label={t("applications.filters.to")}>
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => {
              setPage(1);
              setToDate(e.target.value);
            }}
          />
        </FilterField>
        <select
          className="leaves-filter-input lt-input"
          value={sortKey}
          onChange={(e) => {
            setPage(1);
            setSortKey(e.target.value);
          }}
          aria-label={t("applications.filters.sort")}
        >
          <option value="created_desc">{t("applications.sort.createdDesc")}</option>
          <option value="created_asc">{t("applications.sort.createdAsc")}</option>
          <option value="updated_desc">{t("applications.sort.updatedDesc")}</option>
          <option value="name_asc">{t("applications.sort.nameAsc")}</option>
          <option value="name_desc">{t("applications.sort.nameDesc")}</option>
        </select>
      </FilterBar>

      {loading && <div className="sat-empty">{t("applications.loading")}</div>}
      {!loading && !forbidden && rows.length === 0 && (
        <EmptyState message={hasActiveFilter || statusFilter ? t("applications.emptyFiltered") : t("applications.empty")} />
      )}

      {!loading && rows.length > 0 && (
        <div className="apps-table-wrap">
          <table className="sat-table apps-table">
            <thead>
              <tr>
                <th>{t("applications.columns.candidate")}</th>
                <th>{t("applications.columns.job")}</th>
                <th>{t("applications.columns.type")}</th>
                <th>{t("applications.columns.description")}</th>
                <th>{t("applications.columns.file")}</th>
                <th>{t("applications.columns.date")}</th>
                <th>{t("applications.columns.status")}</th>
                <th>{t("applications.columns.tags")}</th>
                <th>{t("applications.columns.action")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const desc = (r.description || "").trim();
                const descShort = desc.length > 90 ? `${desc.slice(0, 90)}…` : desc;
                return (
                  <tr
                    key={r.id}
                    tabIndex={0}
                    onClick={() => setOpenId(r.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setOpenId(r.id);
                    }}
                  >
                    <td>
                      <div className="apps-name">{r.applicant_name}</div>
                      <div className="sat-hint">{r.applicant_email}</div>
                    </td>
                    <td>{r.external_job_title || `#${r.external_job_id || "-"}`}</td>
                    <td>{r.application_type_name_tr || "-"}</td>
                    <td className="apps-desc" title={desc || ""}>
                      {descShort || <span className="muted">—</span>}
                    </td>
                    <td>
                      {r.has_cv ? (
                        <span className="apps-attach apps-attach--yes" title={r.cv_original_name || t("applications.cvPresent")}>
                          📎 {t("applications.yes")}
                        </span>
                      ) : (
                        <span className="apps-attach apps-attach--no">—</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {new Date(r.created_at).toLocaleDateString("tr-TR")}
                    </td>
                    <td>
                      <StatusChip status={r.status} />
                    </td>
                    <td>
                      <div className="apps-tag-list">
                        {(r.tags || []).slice(0, 3).map((tg) => (
                          <TagChip key={tg.id} tag={tg} />
                        ))}
                        {(r.tags || []).length > 3 && (
                          <span className="sat-hint">+{r.tags.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="sat-btn sat-btn--primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenId(r.id);
                        }}
                      >
                        {t("applications.review")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PaginationControls
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPage(1);
          setPageSize(n);
        }}
      />

      {openId && (
        <ApplicationDetail
          id={openId}
          types={types}
          tags={tags}
          onClose={closeDetail}
          onUpdated={onUpdated}
        />
      )}
    </div>
  );
};

export default AdminApplicationsPage;
